/* ==========================================================================
   Bebysh shared behaviors — namespaced, section-scoped, Theme Editor safe.
   Each section root carries data-bebysh-section="<type>". Init is idempotent:
   roots are flagged with data-bebysh-init so `shopify:section:load` re-inits
   only the reloaded section without duplicating handlers elsewhere.
   ========================================================================== */
window.Bebysh = (function () {
  "use strict";

  var money = function (cents) {
    // Fallback formatter; real prices come pre-formatted from Liquid.
    return "$" + (cents / 100).toFixed(2);
  };

  /* ----------------------------------------------------------------------
     Cart adapter — Dawn-proof (iframe XHR bypass, no fetch patch)
     Mirrors sm-pro.js cartApi (see CHANGELOG 2026-08-28). FurAway reuses the
     same fix: Dawn's storefront-modules patches window.fetch/XHR with ?oseid=
     → 422. The iframe-sourced XHR is the only transport that bypasses it.
     Longer-term: delete the iframe if Dawn's patch can be avoided.
     ---------------------------------------------------------------------- */
  var _byCleanXHR = null;
  function byGetCleanXHR() {
    if (_byCleanXHR) return _byCleanXHR;
    var f = document.createElement("iframe");
    f.style.display = "none";
    document.documentElement.appendChild(f);
    _byCleanXHR = f.contentWindow.XMLHttpRequest;
    return _byCleanXHR;
  }
  function byXhrRequest(method, url, body, cb) {
    var XHR = byGetCleanXHR();
    var xhr = new XHR();
    xhr.open(method, url, true);
    if (body) xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("Accept", "application/json");
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;
      cb(xhr);
    };
    xhr.send(body);
  }
  var cartApi = {
    add: function (items, cb) {
      byXhrRequest(
        "POST",
        "/cart/add.js",
        JSON.stringify({ items: items }),
        cb,
      );
    },
    getSections: function (ids, cb) {
      var url = "/?sections=" + ids.join(",");
      byXhrRequest("GET", url, null, function (xhr) {
        try {
          var data = JSON.parse(xhr.responseText);
          if (data && !data.sections) {
            var wrapped = { sections: {} };
            ids.forEach(function (id) {
              if (data[id] != null) wrapped.sections[id] = data[id];
            });
            if (Object.keys(wrapped.sections).length) data = wrapped;
          }
          cb(xhr, data);
        } catch (e) {
          cb(xhr, null);
        }
      });
    },
  };
  var _byAdding = false;
  function byRefreshAndOpenDrawer(done) {
    var drawer = document.querySelector("cart-drawer");
    function release() {
      _byAdding = false;
      if (typeof done === "function") done();
    }
    if (!drawer || typeof drawer.renderContents !== "function") {
      // No Dawn drawer — fallback toast, no stale open
      release();
      return;
    }
    cartApi.getSections(["cart-drawer"], function (xhr, resp) {
      try {
        if (!resp || !resp.sections) {
          release();
          return;
        }
        drawer.renderContents(resp);
        setTimeout(release, 450);
      } catch (e) {
        release();
      }
    });
  }

  /* ----------------------------------------------------------------------
     Countdown — every [data-countdown-end] root drives nested instances of
     [data-countdown] (chips [data-cd="h|m|s"] or plain HH:MM:SS text).
     ---------------------------------------------------------------------- */
  function initCountdown(root) {
    var endRaw = root.getAttribute("data-countdown-end");
    var instances = root.querySelectorAll("[data-countdown]");
    if (!endRaw || !instances.length) return;

    var cycleMs = parseInt(
      root.getAttribute("data-countdown-cycle") || "0",
      10,
    );
    var cycle = cycleMs * 60 * 1000;
    var end = Date.parse(endRaw);
    if (isNaN(end)) return;
    // Single time: announcement syncs to hero's end/cycle so both tick together
    var isAnnouncement =
      root.getAttribute("data-bebysh-section") === "announcement";
    if (isAnnouncement) {
      var heroEl = document.querySelector('[data-bebysh-section="hero"]');
      if (heroEl) {
        var heroEnd = heroEl.getAttribute("data-countdown-end");
        var heroCycle = parseInt(
          heroEl.getAttribute("data-countdown-cycle") || "0",
          10,
        );
        if (heroEnd && !isNaN(Date.parse(heroEnd))) {
          endRaw = heroEnd;
          end = Date.parse(heroEnd);
          cycleMs = heroCycle;
          cycle = heroCycle * 60 * 1000;
        }
      }
    }

    // Evergreen mode: when cycle > 0, persist a 6h10m (370min) window in localStorage
    // so first visit always shows 06:10:00 and it counts down across reloads.
    // Single global key for FurAway so announcement + hero share one timer (single time).
    var storageKey = "by-countdown-furaway-global-" + cycleMs;
    if (cycle > 0) {
      try {
        var stored = parseInt(localStorage.getItem(storageKey) || "", 10);
        var now = Date.now();
        if (!stored || isNaN(stored) || stored <= now || stored - now > cycle) {
          stored = now + cycle;
          localStorage.setItem(storageKey, String(stored));
        }
        end = stored;
      } catch (e) {
        // localStorage unavailable — fall back to endRaw + cycle modulo
        if (end - Date.now() > cycle) {
          end = Date.now() + cycle;
        }
      }
    }

    function render() {
      var remaining = end - Date.now();
      if (remaining <= 0 && cycle > 0) {
        // Evergreen: restart the window so the offer never shows a dead zero.
        end = Date.now() + cycle;
        try {
          localStorage.setItem(storageKey, String(end));
        } catch (e) {}
        remaining = end - Date.now();
      } else if (cycle > 0 && remaining > cycle) {
        // End far in future (e.g. 2026-12-31) but evergreen requested: clamp to cycle
        remaining = cycle;
        end = Date.now() + cycle;
        try {
          localStorage.setItem(storageKey, String(end));
        } catch (e) {}
      }
      var s = Math.max(0, Math.floor(remaining / 1000));
      var parts = [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60];
      var pad = function (n) {
        return String(n).padStart(2, "0");
      };
      instances.forEach(function (el) {
        var h = el.querySelector('[data-cd="h"]');
        if (h) {
          h.textContent = pad(parts[0]);
          el.querySelector('[data-cd="m"]').textContent = pad(parts[1]);
          el.querySelector('[data-cd="s"]').textContent = pad(parts[2]);
        } else {
          el.textContent =
            pad(parts[0]) + ":" + pad(parts[1]) + ":" + pad(parts[2]);
        }
      });
    }
    render();
    setInterval(render, 1000);
  }

  /* ----------------------------------------------------------------------
     Gallery — main image, arrows, thumbnails, counter, keyboard + touch.
     ---------------------------------------------------------------------- */
  function initGallery(root) {
    var gallery = root.querySelector("[data-bebysh-gallery]");
    if (!gallery) return;
    var main = gallery.querySelector("[data-gallery-main]");
    var thumbs = Array.prototype.slice.call(
      gallery.querySelectorAll("[data-gallery-thumb]"),
    );
    var prev = gallery.querySelector("[data-gallery-prev]");
    var next = gallery.querySelector("[data-gallery-next]");
    var counter = gallery.querySelector("[data-gallery-counter]");
    if (!main || !thumbs.length) return;

    var current = 0;
    function show(i) {
      current = (i + thumbs.length) % thumbs.length;
      var thumb = thumbs[current];
      main.src = thumb.dataset.gallerySrc || main.src;
      main.alt = thumb.dataset.galleryAlt || main.alt;
      thumbs.forEach(function (t, index) {
        if (index === current) {
          t.setAttribute("aria-current", "true");
        } else {
          t.removeAttribute("aria-current");
        }
      });
      if (counter) counter.textContent = current + 1 + " / " + thumbs.length;
    }
    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        show(i);
      });
    });
    if (prev)
      prev.addEventListener("click", function () {
        show(current - 1);
      });
    if (next)
      next.addEventListener("click", function () {
        show(current + 1);
      });
    main.setAttribute("tabindex", "0");
    main.addEventListener("keydown", function (e) {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        show(current + 1);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        show(current - 1);
      }
    });
    var touchX = 0;
    main.addEventListener(
      "touchstart",
      function (e) {
        touchX = e.changedTouches[0].screenX;
      },
      { passive: true },
    );
    main.addEventListener(
      "touchend",
      function (e) {
        var dx = e.changedTouches[0].screenX - touchX;
        if (Math.abs(dx) > 35) show(current + (dx < 0 ? 1 : -1));
      },
      { passive: true },
    );
    show(0);
  }

  /* ----------------------------------------------------------------------
     Purchase panel — bundle (variant) selection, purchase type (selling
     plan), quantity, price sync, sticky ATC sync, AJAX add to cart, toast.
     Phase 5: every selectable card maps to a real purchasable state
     (variant id, optional quantity, optional selling plan). Prices shown
     are always Liquid-rendered real prices (variant price, Liquid-computed
     bundle total, or selling-plan allocation price via data attributes).
     ---------------------------------------------------------------------- */
  function initPurchase(root) {
    var panel = root.querySelector("[data-bebysh-purchase]");
    if (!panel) return;
    var form = panel.querySelector("form[data-bebysh-form]");
    var variantInput = panel.querySelector('[name="id"]');
    var priceEls = panel.querySelectorAll("[data-price]");
    var sticky = root.querySelector("[data-sticky-atc]");
    var stickyPrice = sticky
      ? sticky.querySelector("[data-sticky-price]")
      : null;
    var toast = root.querySelector("[data-bebysh-toast]");

    function showToast(message, isError) {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.toggle("by-toast--error", Boolean(isError));
      toast.hidden = false;
      clearTimeout(toast._byTimer);
      toast._byTimer = setTimeout(function () {
        toast.hidden = true;
      }, 2500);
    }

    function selectedCard() {
      return panel.querySelector('[data-variant-id][aria-pressed="true"]');
    }

    function currentBasePrice() {
      var card = selectedCard();
      return card ? card.dataset.variantPrice || "" : "";
    }

    function applyPrices(price) {
      if (!price) return;
      priceEls.forEach(function (el) {
        el.textContent = price;
      });
      if (stickyPrice) stickyPrice.textContent = price;
    }

    function syncBonuses() {
      var card = selectedCard();
      var qty = 1;
      if (card) {
        if (card.dataset.bonusTier) qty = parseInt(card.dataset.bonusTier, 10);
        else if (card.dataset.quantity)
          qty = parseInt(card.dataset.quantity, 10);
        else {
          var label = (
            card.dataset.label ||
            card.textContent ||
            ""
          ).toLowerCase();
          if (label.indexOf("3") !== -1 && label.indexOf("1") !== -1) qty = 4;
          else if (label.indexOf("buy 2") !== -1 || label.indexOf("2") !== -1)
            qty = 2;
        }
      }
      if (!qty || isNaN(qty)) qty = 1;
      var bonusRoot = root.querySelector("[data-bebysh-bonuses]");
      if (!bonusRoot) bonusRoot = panel;
      bonusRoot.querySelectorAll("[data-bonus-tier]").forEach(function (el) {
        var tier = parseInt(el.getAttribute("data-bonus-tier") || "1", 10);
        if (qty >= tier) el.classList.add("is-active");
        else el.classList.remove("is-active");
      });
    }

    // Bundle cards -> variant id + price + (single-variant mode) quantity
    panel.querySelectorAll("[data-variant-id]").forEach(function (card) {
      card.addEventListener("click", function () {
        if (card.dataset.unavailable === "true") return;
        panel.querySelectorAll("[data-variant-id]").forEach(function (c) {
          c.setAttribute("aria-pressed", String(c === card));
        });
        if (variantInput) variantInput.value = card.dataset.variantId;
        var quantityInput = form
          ? form.querySelector('[name="quantity"]')
          : null;
        if (quantityInput) quantityInput.value = card.dataset.quantity || "1";
        applyPrices(card.dataset.variantPrice);
        syncBonuses();
        var cardPlanSelect = panel.querySelector('select[name="selling_plan"]');
        if (cardPlanSelect && card.dataset.variantPlans) {
          var allowed = card.dataset.variantPlans.split(",").filter(Boolean);
          Array.prototype.forEach.call(cardPlanSelect.options, function (opt) {
            opt.hidden = opt.value !== "" && allowed.indexOf(opt.value) === -1;
          });
          if (
            cardPlanSelect.selectedOptions[0] &&
            cardPlanSelect.selectedOptions[0].hidden
          )
            cardPlanSelect.selectedIndex = 0;
        }
        syncPurchaseType(panel.querySelector('select[name="selling_plan"]'));
      });
    });

    // Init bonus highlight and quantity based on initially selected card
    (function initFromSelected() {
      var sel = selectedCard();
      if (sel && variantInput) variantInput.value = sel.dataset.variantId;
      var quantityInput = form ? form.querySelector('[name="quantity"]') : null;
      if (sel && quantityInput)
        quantityInput.value = sel.dataset.quantity || "1";
      syncBonuses();
      if (sel) applyPrices(sel.dataset.variantPrice);
      // Inventory-aware disable: if hardcoded quantity > stock and policy deny, mark unavailable
      panel.querySelectorAll("[data-variant-id]").forEach(function (card) {
        var q = parseInt(card.dataset.quantity || "1", 10);
        var inv = card.dataset.inventory;
        var pol = card.dataset.policy;
        if (inv !== undefined && inv !== "" && pol === "deny") {
          var invNum = parseInt(inv, 10);
          if (!isNaN(invNum) && q > invNum) {
            card.setAttribute("data-unavailable", "true");
            card.setAttribute("disabled", "");
            card.setAttribute("aria-disabled", "true");
            // Add sold-out hint if not already present
            if (!card.querySelector(".by-bundle__soldout")) {
              var sub = card.querySelector(".by-bundle__sub");
              if (sub)
                sub.innerHTML =
                  '<span class="by-bundle__soldout">Only ' +
                  invNum +
                  " left</span>";
            }
          }
        }
      });
    })();

    // Purchase type buttons <-> selling plan select <-> price synchronization
    function syncPurchaseType(select) {
      var value = select && select.value ? select.value : "";
      panel.querySelectorAll("[data-purchase-type]").forEach(function (btn) {
        var want = btn.dataset.purchaseType; // 'subscribe' | 'onetime'
        var active = want === "subscribe" ? Boolean(value) : !value;
        btn.setAttribute("aria-pressed", String(active));
      });
      var price = "";
      if (select && value) {
        var opt = select.selectedOptions[0];
        price = (opt && opt.dataset.planPrice) || currentBasePrice();
      } else {
        price = currentBasePrice();
      }
      applyPrices(price);
    }
    var planSelect = panel.querySelector('select[name="selling_plan"]');
    if (planSelect) {
      syncPurchaseType(planSelect);
      planSelect.addEventListener("change", function () {
        syncPurchaseType(planSelect);
      });
      panel.querySelectorAll("[data-purchase-type]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.dataset.purchaseType === "subscribe") {
            var firstVisible = Array.prototype.find.call(
              planSelect.options,
              function (o) {
                return o.value && !o.hidden;
              },
            );
            if (firstVisible) planSelect.value = firstVisible.value;
          } else {
            planSelect.value = "";
          }
          planSelect.dispatchEvent(new Event("change"));
        });
      });
    }

    // Sticky add-to-cart mirrors the main form (same variant, plan, quantity)
    var stickyBtn = sticky ? sticky.querySelector("[data-sticky-add]") : null;
    if (stickyBtn && form) {
      stickyBtn.addEventListener("click", function () {
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    }

    // Reveal sticky bar once the hero gallery scrolls out of view
    if (sticky) {
      var target =
        root.querySelector("[data-bebysh-gallery]") ||
        root.querySelector(".by-hero__grid");
      if (target && "IntersectionObserver" in window) {
        new IntersectionObserver(
          function (entries) {
            sticky.classList.toggle(
              "by-sticky-atc--visible",
              !entries[0].isIntersecting,
            );
          },
          { threshold: 0 },
        ).observe(target);
      } else {
        sticky.classList.add("by-sticky-atc--visible");
      }
    }

    // AJAX add to cart with real Shopify endpoints + section rerender
    if (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var selected = selectedCard();
        if (selected && selected.dataset.unavailable === "true") {
          showToast("This option is currently sold out.", true);
          return;
        }
        var submitBtn = form.querySelector('[type="submit"]');
        if (
          submitBtn &&
          (submitBtn.disabled ||
            submitBtn.getAttribute("aria-disabled") === "true")
        )
          return;
        if (submitBtn) submitBtn.setAttribute("aria-disabled", "true");

        // Ensure hidden id matches selected card (fallback if Theme Editor changed selection after load)
        var sel = selectedCard();
        if (sel && variantInput) variantInput.value = sel.dataset.variantId;
        var quantityInput = form.querySelector('[name="quantity"]');
        if (sel && quantityInput)
          quantityInput.value =
            sel.dataset.quantity || quantityInput.value || "1";
        var id = variantInput ? variantInput.value : null;
        if (!id || isNaN(Number(id))) {
          showToast("Please select a valid option.", true);
          if (submitBtn) submitBtn.removeAttribute("aria-disabled");
          return;
        }
        var qtyVal = parseInt(quantityInput ? quantityInput.value : "1", 10);
        if (!qtyVal || isNaN(qtyVal) || qtyVal < 1) qtyVal = 1;
        if (quantityInput) quantityInput.value = String(qtyVal);
        // Inventory guard: single-variant qty 2/4 may exceed stock (hardcoded 2 issue)
        if (
          sel &&
          sel.dataset.inventory !== undefined &&
          sel.dataset.inventory !== "" &&
          sel.dataset.policy === "deny"
        ) {
          var invNum = parseInt(sel.dataset.inventory, 10);
          if (!isNaN(invNum) && qtyVal > invNum) {
            showToast(
              "Only " +
                invNum +
                " in stock for this option — requested " +
                qtyVal +
                ". Choose a smaller bundle or restock. (hardcoded quantity exceeds inventory)",
              true,
            );
            if (submitBtn) submitBtn.removeAttribute("aria-disabled");
            return;
          }
        }
        if (_byAdding) return;
        _byAdding = true;
        var item = { id: Number(id), quantity: qtyVal };
        var plan = form.querySelector('select[name="selling_plan"]');
        if (!plan) plan = form.querySelector('[name="selling_plan"]');
        if (plan && plan.value) item.selling_plan = Number(plan.value);
        var items = [item];
        try {
          console.debug(
            "[Bebysh ATC] payload",
            { items: items },
            "card",
            sel ? sel.dataset : null,
          );
        } catch (e) {}
        if (submitBtn) submitBtn.setAttribute("aria-busy", "true");
        cartApi.add(items, function (xhr) {
          try {
            var data = JSON.parse(xhr.responseText || "{}");
            if (xhr.status >= 200 && xhr.status < 300) {
              updateCartCounts();
              publishCartUpdate(data);
              var label =
                selected && selected.dataset.label
                  ? selected.dataset.label + " \u00b7 "
                  : "";
              var price = selected ? selected.dataset.variantPrice || "" : "";
              showToast("Added to cart \u2014 " + label + price, false);
              byRefreshAndOpenDrawer(function () {
                if (submitBtn) {
                  submitBtn.removeAttribute("aria-disabled");
                  submitBtn.removeAttribute("aria-busy");
                }
              });
            } else {
              var msg =
                (data && (data.description || data.message)) ||
                "Could not add to cart.";
              showToast(msg, true);
              _byAdding = false;
              if (submitBtn) {
                submitBtn.removeAttribute("aria-disabled");
                submitBtn.removeAttribute("aria-busy");
              }
            }
          } catch (e2) {
            showToast("Could not add to cart.", true);
            _byAdding = false;
            if (submitBtn) {
              submitBtn.removeAttribute("aria-disabled");
              submitBtn.removeAttribute("aria-busy");
            }
          }
        });
        return;
      });
    }
  }

  function updateCartCounts() {
    byXhrRequest("GET", "/cart.js", null, function (xhr) {
      try {
        if (xhr.status < 200 || xhr.status >= 300) return;
        var cart = JSON.parse(xhr.responseText);
        document.querySelectorAll("[data-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll("[data-cart-status]").forEach(function (el) {
          el.textContent =
            cart.item_count === 1
              ? "Cart updated: 1 item"
              : "Cart updated: " + cart.item_count + " items";
        });
        document.querySelectorAll("[data-cart-link]").forEach(function (el) {
          el.setAttribute(
            "aria-label",
            "Cart, " +
              cart.item_count +
              (cart.item_count === 1 ? " item" : " items"),
          );
        });
      } catch (e) {}
    });
  }

  function openCartDrawer(sectionData) {
    var drawer = document.querySelector("cart-drawer");
    if (
      drawer &&
      typeof drawer.renderContents === "function" &&
      sectionData &&
      sectionData.sections
    ) {
      drawer.renderContents(sectionData);
    } else if (drawer) {
      byRefreshAndOpenDrawer();
    }
  }

  /* ----------------------------------------------------------------------
     Dawn pub/sub integration — keeps every [data-cart-count] badge in sync
     with cart changes made anywhere (drawer quantity edits, removals, and
     our own add-to-cart).
     ---------------------------------------------------------------------- */
  function publishCartUpdate(data) {
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, data || {});
    }
  }

  function subscribeCartUpdates() {
    if (
      typeof subscribe !== "function" ||
      typeof PUB_SUB_EVENTS === "undefined"
    )
      return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      updateCartCounts();
    });
  }

  /* ----------------------------------------------------------------------
     Review filter tabs
     ---------------------------------------------------------------------- */
  function initReviewFilters(root) {
    var tabs = root.querySelectorAll("[data-review-filter]");
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        tabs.forEach(function (t) {
          t.setAttribute("aria-pressed", String(t === tab));
        });
        var filter = tab.dataset.reviewFilter;
        root
          .querySelectorAll("[data-review-category]")
          .forEach(function (card) {
            card.hidden =
              filter !== "all" && card.dataset.reviewCategory !== filter;
          });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Adopted carousel — avatar tabs swap the visible quote panel.
     Implements the ARIA tabs pattern: roving tabindex, ArrowLeft/Right and
     Home/End keyboard support (plan §7).
     ---------------------------------------------------------------------- */
  function initAdopted(root) {
    var tabs = Array.prototype.slice.call(
      root.querySelectorAll("[data-adopted-tab]"),
    );
    var panels = root.querySelectorAll("[data-adopted-panel]");
    if (!tabs.length || !panels.length) return;

    function select(index) {
      tabs.forEach(function (t, n) {
        var active = n === index;
        t.setAttribute("aria-selected", String(active));
        t.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function (p, n) {
        p.hidden = n !== index;
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener("click", function () {
        select(i);
      });
      tab.addEventListener("keydown", function (e) {
        var target = null;
        if (e.key === "ArrowRight") target = (i + 1) % tabs.length;
        else if (e.key === "ArrowLeft")
          target = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === "Home") target = 0;
        else if (e.key === "End") target = tabs.length - 1;
        if (target === null) return;
        e.preventDefault();
        select(target);
        tabs[target].focus();
      });
    });
    select(0);
  }

  /* ----------------------------------------------------------------------
     Before/After — range input clips the "after" layer
     ---------------------------------------------------------------------- */
  function initCompare(root) {
    root.querySelectorAll("[data-compare]").forEach(function (frame) {
      var range = frame.querySelector(".by-compare__range");
      var after = frame.querySelector(".by-compare__after");
      var line = frame.querySelector(".by-compare__line");
      if (!range || !after) return;
      function apply() {
        var v = range.value + "%";
        after.style.clipPath = "inset(0 " + (100 - range.value) + "% 0 0)";
        if (line) line.style.left = v;
      }
      range.addEventListener("input", apply);
      // Visible focus ring fallback for browsers without :has() (plan §7).
      range.addEventListener("focus", function () {
        frame.classList.add("by-compare__frame--focused");
      });
      range.addEventListener("blur", function () {
        frame.classList.remove("by-compare__frame--focused");
      });
      apply();
    });
  }

  /* ----------------------------------------------------------------------
     Video testimonials — poster -> inline video, no eager autoplay
     ---------------------------------------------------------------------- */
  function initVideos(root) {
    root.querySelectorAll("[data-video-toggle]").forEach(function (button) {
      button.addEventListener("click", function () {
        var video = button.parentElement.querySelector("video");
        button.hidden = true;
        if (video) {
          video.hidden = false;
          video.play().catch(function () {});
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     Header — search panel toggle with Shopify predictive search, cart
     drawer hand-off, mobile menu auto-close on outside click.
     ---------------------------------------------------------------------- */
  var menuHandlerBound = false;

  function initHeader(root) {
    var toggle = root.querySelector("[data-search-toggle]");
    var panel = root.querySelector("[data-search-panel]");
    var close = root.querySelector("[data-search-close]");
    if (toggle && panel) {
      toggle.setAttribute("aria-expanded", "false");
      toggle.addEventListener("click", function () {
        var open = panel.hidden;
        panel.hidden = !open;
        toggle.setAttribute("aria-expanded", String(open));
        if (open) {
          var input = panel.querySelector('input[name="q"]');
          if (input) input.focus();
        }
      });
    }
    if (close && panel)
      close.addEventListener("click", function () {
        panel.hidden = true;
      });

    // Escape closes the search panel and restores focus to the toggle (plan §7).
    if (panel) {
      panel.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          panel.hidden = true;
          if (toggle) {
            toggle.setAttribute("aria-expanded", "false");
            toggle.focus();
          }
        }
      });
    }

    // Cart icon opens the Dawn cart drawer when enabled; falls back to /cart.
    var cartLink = root.querySelector("[data-cart-link]");
    if (cartLink) {
      cartLink.addEventListener("click", function (e) {
        var drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.open === "function") {
          e.preventDefault();
          drawer.open();
        }
      });
    }

    // Mobile menu: aria-expanded on the summary, Escape closes and refocuses,
    // and one global handler closes any open menu on outside click (plan §7).
    root.querySelectorAll("details[data-mobile-menu]").forEach(function (menu) {
      var summary = menu.querySelector("summary");
      if (!summary) return;
      var syncExpanded = function () {
        summary.setAttribute("aria-expanded", String(menu.open));
      };
      syncExpanded();
      summary.addEventListener("click", function () {
        setTimeout(syncExpanded);
      });
      menu.addEventListener("keyup", function (e) {
        if (e.key === "Escape" && menu.open) {
          menu.open = false;
          syncExpanded();
          summary.focus();
        }
      });
    });
    if (!menuHandlerBound) {
      menuHandlerBound = true;
      document.addEventListener("click", function (e) {
        document
          .querySelectorAll("details[data-mobile-menu][open]")
          .forEach(function (menu) {
            if (!menu.contains(e.target)) {
              menu.open = false;
              var summary = menu.querySelector("summary");
              if (summary) summary.setAttribute("aria-expanded", "false");
            }
          });
      });
    }

    // Predictive search against the Shopify search endpoint.
    var input = root.querySelector("[data-predictive-input]");
    var results = root.querySelector("[data-search-results]");
    if (input && results) {
      var timer = null;
      input.addEventListener("input", function () {
        clearTimeout(timer);
        var term = input.value.trim();
        if (term.length < 2) {
          results.hidden = true;
          results.innerHTML = "";
          return;
        }
        timer = setTimeout(function () {
          var base =
            window.Shopify && window.Shopify.routes
              ? window.Shopify.routes.root
              : "/";
          var url =
            base +
            "search/suggest.json?q=" +
            encodeURIComponent(term) +
            "&resources[type]=product&resources[limit]=5";
          fetch(url, { headers: { Accept: "application/json" } })
            .then(function (r) {
              return r.json();
            })
            .then(function (data) {
              var products =
                (data &&
                  data.resources &&
                  data.resources.results &&
                  data.resources.results.products) ||
                [];
              results.innerHTML = "";
              if (!products.length) {
                results.hidden = true;
                return;
              }
              products.forEach(function (p) {
                var item = document.createElement("a");
                item.className = "by-search-result";
                item.href = p.url;
                var img = document.createElement("img");
                img.src = p.image || "";
                img.alt = "";
                img.loading = "lazy";
                item.appendChild(img);
                var title = document.createElement("span");
                title.className = "by-search-result__title";
                title.textContent = p.title;
                item.appendChild(title);
                var price = document.createElement("span");
                price.className = "by-search-result__price";
                price.textContent = p.price
                  ? money(Math.round(p.price * 100))
                  : "";
                item.appendChild(price);
                results.appendChild(item);
              });
              results.hidden = false;
            })
            .catch(function () {
              results.hidden = true;
            });
        }, 300);
      });
      input.addEventListener("keydown", function (e) {
        if (e.key === "Escape") results.hidden = true;
      });
      document.addEventListener("click", function (e) {
        if (!results.hidden && !e.target.closest("[data-search-panel]"))
          results.hidden = true;
      });
    }
  }

  /* ----------------------------------------------------------------------
     Guarantee / secondary CTAs trigger the hero product form
     ---------------------------------------------------------------------- */
  function initCtaTriggers(root) {
    root.querySelectorAll("[data-atc-trigger]").forEach(function (el) {
      el.addEventListener("click", function (e) {
        var form = document.querySelector("form[data-bebysh-form]");
        if (form) {
          e.preventDefault();
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     Section registry + idempotent init
     ---------------------------------------------------------------------- */
  var initizers = {
    announcement: initCountdown,
    header: initHeader,
    hero: function (root) {
      initCountdown(root);
      initGallery(root);
      initPurchase(root);
    },
    "mini-faq": function () {},
    "mini-reviews": function () {},
    steps: function () {},
    "usp-bar": function () {},
    marquee: function () {},
    adopted: initAdopted,
    "before-after": initCompare,
    "video-testimonials": initVideos,
    "why-choose": function () {},
    stats: function () {},
    guide: function () {},
    reviews: initReviewFilters,
    guarantee: initCtaTriggers,
    faq: function () {},
    footer: function () {},
  };

  function initSection(el) {
    if (!el || el.dataset.bebyshInit === "1") return;
    el.dataset.bebyshInit = "1";
    var type = el.getAttribute("data-bebysh-section");
    var fn = initizers[type] || initCountdown;
    fn(el);
    // Countdown may exist in any section that declares an end time.
    if (type !== "announcement" && type !== "hero") initCountdown(el);
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll("[data-bebysh-section]").forEach(initSection);
  }

  document.addEventListener("DOMContentLoaded", function () {
    init(document);
  });
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
  subscribeCartUpdates();

  return { init: init, money: money, updateCartCounts: updateCartCounts };
})();
